import {
  AddIcon,
  Alert,
  Button,
  DatePicker,
  Dialog,
  FormSection,
  FormStack,
  MenuItem,
  SelectField,
  Stack,
  TextField,
} from '@all-manager/design-system-v2';
import { startOfDay } from 'date-fns';
import { Controller } from "react-hook-form";
import DescriptionField from "../../../../components/modals/components/DescriptionFieldOk";
import { ReminderDatabase, type ReminderRecurringType } from "../../../../types";
import ProviderAutocomplete from "../../../providers/external-components/ProviderAutocomplete/ProviderAutocomplete";
import SubTopicAutocomplete from "../../../topics/external-components/SubTopicAutocomplete/SubTopicAutocomplete";
import recurrenceInFrench from "../../utils/recurrenceInFrench";
import useAddUpdateReminder from "./useAddUpdateReminder";

type Props = {
  onClose: () => void;
  providerId?: string;
  subjectId?: string;
  reminder?: ReminderDatabase;
  afterSuccess?: () => void;
  navigatePath?: string;
  displayProviderField?: boolean;
  displaySubjectField?: boolean;
}

const RECURRENCE_TYPES: ReminderRecurringType[] = ['weekly', 'monthly', 'yearly'];

export default function AddUpdateReminder(props: Props) {
  const {
    form,
    handleConfirm,
    isSubmitting,
    showDescription,
    handleAddRecurrence,
    handleRemoveRecurrence,
    handleAddDescription,
    serverError,
  } = useAddUpdateReminder(props);

  const isRecurring = form.watch("is_recurring") === "recurring";
  const rootError = form.formState.errors.root?.message;

  return (
    <Dialog
      open
      onClose={props.onClose}
      onSubmit={handleConfirm}
      maxWidth="sm"
      fullWidth
      title={props.reminder ? "Modifier le rappel" : "Nouveau rappel"}
      closeLabel="Fermer"
      actions={{
        primaryAction: {
          label: props.reminder ? "Enregistrer le rappel" : "Créer le rappel",
          type: 'submit',
          loading: isSubmitting,
        },
        secondaryAction: {
          label: 'Annuler',
          onClick: props.onClose,
          disabled: isSubmitting,
        },
      }}
    >
      <Stack spacing={3}>
        {serverError ? <Alert severity="error">{serverError}</Alert> : null}
        {rootError ? <Alert severity="error">{rootError}</Alert> : null}

        <FormStack>
          <Controller
            control={form.control}
            name="title"
            render={({ field, fieldState }) => (
              <TextField
                name={field.name}
                inputRef={field.ref}
                onBlur={field.onBlur}
                onChange={field.onChange}
                value={field.value ?? ''}
                label="Titre"
                fullWidth
                required
                autoFocus
                error={!!fieldState.error}
                helperText={fieldState.error?.message}
              />
            )}
          />

          <Controller
            control={form.control}
            name="due_date"
            render={({ field, fieldState }) => (
              <DatePicker
                label="Date d'échéance"
                value={field.value}
                onChange={(date) => {
                  if (date) {
                    field.onChange(startOfDay(date));
                  }
                }}
                inputRef={field.ref}
                required
                fullWidth
                error={!!fieldState.error}
                helperText={fieldState.error?.message}
              />
            )}
          />

          {props.displaySubjectField ? (
            <Controller
              control={form.control}
              name="subject_id"
              render={({ field, fieldState }) => (
                <SubTopicAutocomplete
                  {...field}
                  label="Sujet lié"
                  fullWidth
                  error={!!fieldState.error}
                  helperText={fieldState.error?.message}
                />
              )}
            />
          ) : null}

          {props.displayProviderField ? (
            <Controller
              control={form.control}
              name="provider_id"
              render={({ field, fieldState }) => (
                <ProviderAutocomplete
                  {...field}
                  label="Prestataire"
                  fullWidth
                  error={!!fieldState.error}
                  helperText={fieldState.error?.message}
                />
              )}
            />
          ) : null}
        </FormStack>

        {isRecurring ? (
          <FormSection title="Récurrence">
            <FormStack direction="row">
              <Controller
                control={form.control}
                name="recurrence_value"
                render={({ field, fieldState }) => (
                  <TextField
                    name={field.name}
                    inputRef={field.ref}
                    onBlur={field.onBlur}
                    onChange={(event) => {
                      const value = event.target.value.trim();
                      field.onChange(value === '' ? null : Number(value));
                    }}
                    value={field.value ?? ''}
                    label="Répéter tou(te)s les"
                    type="number"
                    fullWidth
                    required
                    error={!!fieldState.error}
                    helperText={fieldState.error?.message}
                  />
                )}
              />

              <Controller
                control={form.control}
                name="recurrence_type"
                render={({ field, fieldState }) => (
                  <SelectField<ReminderRecurringType>
                    name={field.name}
                    inputRef={field.ref}
                    onBlur={field.onBlur}
                    onChange={(event) => field.onChange(event.target.value)}
                    value={field.value}
                    label="Période"
                    fullWidth
                    required
                    error={!!fieldState.error}
                    helperText={fieldState.error?.message}
                  >
                    {RECURRENCE_TYPES.map((type) => (
                      <MenuItem key={type} value={type}>
                        {recurrenceInFrench(type)}
                      </MenuItem>
                    ))}
                  </SelectField>
                )}
              />
            </FormStack>

            <Stack direction="row">
              <Button variant="text" size="small" color="error" onClick={handleRemoveRecurrence}>
                Retirer la récurrence
              </Button>
            </Stack>
          </FormSection>
        ) : null}

        {showDescription ? (
          <DescriptionField
            form={form}
            name="description"
            label="Description"
          />
        ) : null}

        {!isRecurring || !showDescription ? (
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ xs: 'flex-start', sm: 'center' }}>
            {!isRecurring ? (
              <Button variant="text" color="default" startIcon={<AddIcon />} onClick={handleAddRecurrence}>
                Ajouter une récurrence
              </Button>
            ) : null}
            {!showDescription ? (
              <Button variant="text" color="default" startIcon={<AddIcon />} onClick={handleAddDescription}>
                Ajouter une description
              </Button>
            ) : null}
          </Stack>
        ) : null}
      </Stack>
    </Dialog>
  );
}
